// Файл api.js

import axios from 'axios';

import { download_blob } from '../utils/download_blob';
import {
    validate_df_request,
    validate_df_response,
} from './api_parts/api_contracts';
import {
    configure_csrf,
    refresh_csrf_token,
    reset_csrf_token,
} from './csrf';



const IS_LOCAL_FRONTEND = typeof window === 'undefined'
    || ['127.0.0.1', 'localhost'].includes(window.location.hostname);
const API_BASE_URL = IS_LOCAL_FRONTEND
    ? 'http://127.0.0.1:57272'
    : window.location.origin;
const apiClient = axios.create({
    baseURL: API_BASE_URL,
    timeout: 100000,
    withCredentials: true,
    headers: {
        'Content-Type': 'application/json',
    }
});
const uploadClient = axios.create({
    baseURL: API_BASE_URL,
    timeout: 300000,
    withCredentials: true,
});
configure_csrf(apiClient, API_BASE_URL);
configure_csrf(uploadClient, API_BASE_URL);


const file_name_from_disposition = (disposition) => {
    if (!disposition) {
        return null;
    }

    const utf8_match = disposition.match(
        /filename\*\s*=\s*UTF-8''([^;]+)/i
    );

    if (utf8_match?.[1]) {
        try {
            return decodeURIComponent(utf8_match[1]);

        } catch {
            return utf8_match[1];
        }
    }

    const quoted_match = disposition.match(
	/filename\s*=\s*"([^"]+)"/i);

    if (quoted_match?.[1]) {
        return quoted_match[1];
    }

    const plain_match = disposition.match(
	/filename\s*=\s*([^;]+)/i);

    return plain_match?.[1]?.trim() || null;
};


const sanitize_file_name = (name, fallback = 'download') => {
    let file_name = String(name || fallback).trim();

    file_name = file_name.replace(
	/["<>|:*?\\/]+/g, '_');
    file_name = file_name.replace(
	/[\u0000-\u001F\u007F]/g, '_');
    file_name = file_name.replace(
	/\s+/g, ' ').trim();

    return file_name || fallback;
};


const request_file_response = async (method, func_name, payload = {}) => (
    apiClient.request({
        method,
        url: `/api/${func_name}`,
        ...(method === 'GET' ? { params: payload } : { data: payload }),
        responseType: 'blob',
    })
);


const download_file_response = (response, fallback_name) => {
    const disposition = response.headers?.['content-disposition'];
    const content_type = response.headers?.['content-type']
        || 'application/octet-stream';
    const header_name = file_name_from_disposition(disposition);
    const final_name = sanitize_file_name(
	header_name || fallback_name);
    const blob = response.data instanceof Blob
        ? response.data
        : new Blob([response.data], { type: content_type });

    download_blob(blob, final_name);
};


export const apiService = {
    reset_csrf_token,

    async refresh_csrf_token() {
        return refresh_csrf_token(API_BASE_URL);
    },

    async check_auth({ timeout = 10000, signal } = {}) {
        const response = await apiClient.get(
                '/api/check_auth', {
                timeout,
                ...(signal ? { signal } : {}),
            }
        );
        
        return response.data;
    },

    async df({
        table_name,
        params,
        table_data,
        table_columns,
        table_path,
        save_params,
        timeout,
        signal,
        require_download_access = false,
    }) {
        const endpoint_name = validate_df_request(
            { table_name });
        const response = await apiClient.post(
            `/api/${endpoint_name}`,
            {
                params: params,
                table_data: table_data,
                table_columns: table_columns,
                table_path: table_path,
                save_params: save_params,
                require_download_access: require_download_access,
            },
            {
                ...(timeout ? { timeout } : {}),
                ...(signal ? { signal } : {}),
            }
        );

        return validate_df_response(endpoint_name, response.data);
    },

    async uploadReceipt(func_name, formData) {
        const response = await uploadClient.post(
            `/api/${func_name}`,
            formData
        );

        return response.data;
    },

    async get_file_blob(func_name, params = {}) {
        const response = await request_file_response(
            'GET',
            func_name,
            params,
        );
        return response.data;
    },

    async get_file_blob_POST(func_name, data = {}) {
        const response = await request_file_response(
            'POST',
            func_name,
            data,
        );
        return response.data;
    },

    async download_file(func_name, file_name, params = {}) {
        const response = await request_file_response(
            'GET',
            func_name,
            params,
        );

        download_blob(response.data, file_name);
    },

    async download_file_POST(func_name, file_name, data = {}) {
        const response = await request_file_response(
            'POST',
            func_name,
            data,
        );

        download_file_response(response, file_name);
    },

};


export { API_BASE_URL };